// React and related imports
import React, { useState } from 'react';

// Custom components
import { PageHeader } from '../components/Shared/PageHeader';
import { Loading } from '../components/Shared/Loading/Loading';
import { AnswerModal } from '../components/AnswerModal/AnswerModal';
import { AnswerTable } from '../components/AnswerTable/AnswerTable';

// i18 translations
import { useTranslation } from 'react-i18next';

// RTK Query hooks
import { useFetchAnswersQuery } from '../redux/api/flowApi';

// Interfaces
import { IAnswer } from '../common/interfaces/Answer.interface';

// Constants
import { INITIAL_ANSWER_STATE } from './../common/constants/initialAnswerState';

// Hooks
import { usePageTitle } from '../hooks/usePageTitle';
import { usePagination } from '../hooks/usePagination';

// Pagination
import { CustomPagination } from '../components/Shared/CustomPagination/CustomPagination';
import { paginate } from '../utils/paginate';

export const AnswersPage: React.FC = () => {
  const { t } = useTranslation();

  usePageTitle(t('pageTitles.answers'));

  const [showModal, setShowModal] = useState(false);
  const [isModalForEdit, setIsModalForEdit] = useState(false);
  const [selectedAnswerForEdit, setSelectedAnswerForEdit] =
    useState<IAnswer>(INITIAL_ANSWER_STATE);

  const { data, isLoading } = useFetchAnswersQuery();

  // The endpoint returns every answer; page the (small) list here.
  const { currentPage, setCurrentPage, size } = usePagination(10);
  const answers = paginate(data?.data || [], currentPage, size);

  const handleEditAnswer = (answer: IAnswer) => {
    setSelectedAnswerForEdit(answer);
    setIsModalForEdit(true);
    setShowModal(true);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="p-6">
      <PageHeader
        title={t('content.answers')}
        buttonText={t('content.addAnswer')}
        buttonOnClick={() => {
          setShowModal(true);
          setIsModalForEdit(false);
        }}
      />
      <AnswerTable
        answersList={answers.pageItems}
        handleEditAnswer={handleEditAnswer}
      />
      <CustomPagination
        currentPage={answers.currentPage}
        onPageChange={setCurrentPage}
        totalPages={answers.totalPages}
      />
      {showModal && (
        <AnswerModal
          isModalForAdd={!isModalForEdit}
          answer={isModalForEdit ? selectedAnswerForEdit : INITIAL_ANSWER_STATE}
          show={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};
